import React, { useMemo, useState } from 'react';
import { Box, Card, Grid, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Input } from '@api/Inputs/Input';
import { currencyFormatter } from '@utils/Numbers';

interface InputsGridProps {
    inputs: Input[];
    categoryIdSelected?: string;
    canModify: boolean;
    onAddItem: (input: Input) => void;
    /**
     * Stock ACTUAL de la sucursal por insumo, en unidades base (2026-08-19).
     *
     * Por qué está aquí: las sucursales solo tienen renglón de los insumos que
     * realmente manejan, así que la lista de inventario ya no sirve para saber
     * "de qué me estoy quedando sin". Ese trabajo pasa a esta pantalla, que es
     * donde se decide QUÉ PEDIR: cada insumo muestra lo que hay hoy.
     */
    stockByInput?: Map<string, { totalUnits: number; unit: string }>;
}

export const InputsGrid: React.FunctionComponent<InputsGridProps> = ({
    inputs,
    categoryIdSelected,
    canModify,
    onAddItem,
    stockByInput
}) => {
    const [searchQuery, setSearchQuery] = useState<string>('');

    const filteredInputs = useMemo(() => {
        let result = inputs;
        if (categoryIdSelected) {
            result = result.filter((input) => input.inputCategoryId === categoryIdSelected);
        }
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter((input) => input.name.toLowerCase().includes(query));
        }
        return result;
    }, [inputs, categoryIdSelected, searchQuery]);

    return (
        <Box overflow="auto" p={2} sx={{ position: 'relative', height: '100%' }}>
            <Grid container spacing={2}>
                {filteredInputs.map((input) => (
                    <Grid item xs={12} sm={6} md={3} key={input.id}>
                        <Card
                            variant="outlined"
                            sx={{
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: canModify ? 'pointer' : 'default',
                                opacity: canModify ? 1 : 0.7
                            }}
                            onClick={() => canModify && onAddItem(input)}
                        >
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {input.name}
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                {input.presentation.value} {input.presentation.unit}
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                {currencyFormatter(input.costOfSale)}
                            </Typography>
                            {stockByInput && (
                                <Typography
                                    variant="caption"
                                    fontWeight={700}
                                    // Sin existencia y en negativo se ven distinto: son
                                    // justo los que hay que pedir
                                    color={(() => {
                                        const stock = stockByInput.get(input.id || '');
                                        if (!stock || stock.totalUnits <= 0) {
                                            return 'error.main';
                                        }
                                        return 'success.main';
                                    })()}
                                >
                                    {(() => {
                                        const stock = stockByInput.get(input.id || '');
                                        if (!stock) {
                                            return 'Sin existencia';
                                        }
                                        return `Tienes ${stock.totalUnits} ${stock.unit}`;
                                    })()}
                                </Typography>
                            )}
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box
                sx={{
                    position: 'fixed',
                    bottom: 32,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                    width: '400px',
                    maxWidth: '90vw'
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar insumo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                        sx: {
                            borderRadius: '20px',
                            backgroundColor: 'background.paper',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                '& fieldset': {
                                    border: 'none'
                                }
                            }
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default InputsGrid;
